import React, { useMemo } from 'react';
import {
  PresentationCol,
  PresentationGrid,
  PresentationRow,
} from '../../../layout/PresentationLayout';
import PaymentSummary from './PaymentSummary';
import PaymentMethodSelector from '../PaymentMethodSelector';
import { PaymentForm } from './PaymentForm';
import { useSelector } from 'react-redux';
import {
  selectPaymentID,
  selectPaymentSignatory,
  selectSelectedPaymentMethod,
  selectPaymentFees,
} from '../../../../store/slices/payment/paymentSelectors';
import {
  selectConfigSettings,
  selectConfigPaymentDisclaimerText,
} from '../../../../store/slices/config/configSelectors';
import { selectCurrentUserEmail } from '../../../../store/slices/auth/authSelectors';
import IconIndicator from '../../../user-feedback/IconIndicator';
import { PAYMENT_PROVIDER } from '../../constants';
import Notice from '../../../user-feedback/Notice';
import { FLOWS_ENUM } from 'src/common/enums';

interface PaymentProps {
  flow: FLOWS_ENUM;
  onSuccess: (isGeneric: boolean) => void;
  permitIdentifier?: string;
  permitIdentifiers?: string[];
  singlePaymentButton?: boolean;
}

export const Payment = ({
  flow,
  onSuccess,
  permitIdentifier,
  permitIdentifiers = undefined,
  singlePaymentButton = false,
}: PaymentProps) => {
  const resolvedPermitIdentifiers =
    permitIdentifiers ?? (permitIdentifier ? [permitIdentifier] : []);
  const paymentMethod = useSelector(selectSelectedPaymentMethod);
  const settings = useSelector(selectConfigSettings);
  const processor = settings.payment_processor;
  const paymentProvider = settings.payment_provider;
  const userEmail = useSelector(selectCurrentUserEmail);
  const signatoryName = useSelector(selectPaymentSignatory);
  const paymentID = useSelector(selectPaymentID);
  const paymentFees = useSelector(selectPaymentFees);
  const paymentDisclaimerText = useSelector(selectConfigPaymentDisclaimerText);

  // Kala and SnapPay need to know whether the user chose CC or ACH, so they
  // can't be merged into a single button like other generic processors.
  const showSinglePaymentButton =
    singlePaymentButton &&
    paymentProvider !== PAYMENT_PROVIDER.KALA &&
    paymentProvider !== PAYMENT_PROVIDER.SNAPPAY;

  const hasData = useMemo(() => {
    return !!userEmail && !!signatoryName && !!paymentID && !!paymentMethod;
  }, [userEmail, signatoryName, paymentID, paymentMethod]);

  return (
    <PresentationGrid fluid>
      <PresentationRow>
        <PresentationCol
          xs={24}
          lg={8}
        >
          <PaymentSummary />
        </PresentationCol>
        <PresentationCol
          xs={24}
          lg={16}
        >
          <div className="mb-20">
            <PaymentMethodSelector
              singlePaymentButton={showSinglePaymentButton}
            />
          </div>
          {paymentDisclaimerText && (
            <Notice
              type="info"
              message={paymentDisclaimerText}
              className="mb-20"
            />
          )}
          {hasData && (
            <PaymentForm
              onSuccess={onSuccess}
              processor={processor!}
              paymentProvider={paymentProvider}
              paymentMethod={paymentMethod!}
              flow={flow}
              paymentID={String(paymentID!)}
              signatoryName={signatoryName!}
              userEmail={userEmail!}
              fees={paymentFees!}
              permitIdentifiers={resolvedPermitIdentifiers}
            />
          )}
          {!hasData && <IconIndicator />}
        </PresentationCol>
      </PresentationRow>
    </PresentationGrid>
  );
};

export default Payment;
